import * as angular from 'angular';
import * as _ from 'lodash';

import {
    groupIdentifierSeparator,
    tagDefaultState,
} from '../../common/constants';
import {
    ComponentDefinition,
    Dashboard,
    DisplayObject,
    GuidObjectType,
    IValueViewModel,
    Pattern,
    VisibleObjectBase,
} from '../../models';
import {
    Store,
    StoreElement
} from '../../stateManagement';
import {
    IStoreService
} from '../../services';

class ApplicationSearchController implements angular.IComponentController {
    criteria: Pattern<DisplayObject>[];
    isLoading: boolean;
    loadingMessage: string;
    maxItems = 50;
    objects: DisplayObject[];

    private _unsubscribe: () => void;

    static $inject = [
        '$element',
        '$state',
        'storeService'
    ];

    constructor(
        private _$element: angular.IAugmentedJQuery,
        private _$state: angular.ui.IStateService,
        private _storeService: IStoreService
    ) {
    }

    $onInit() {
        this.criteria = [
            {
                placeholder: 'Dashboard',
                isMatch: (o, reg) => {
                    return o.type === GuidObjectType.Dashboard && reg.test(o.name);
                },
                label: 'Dashboard',
                model: 'd',
                property: 'd'
            },
            {
                placeholder: 'Group',
                isMatch: (o, reg) => {
                    return o.type === GuidObjectType.Group && reg.test(o.name);
                },
                label: 'Group',
                model: 'g',
                property: 'g'
            },
            {
                placeholder: 'Identifier',
                isMatch: (o, reg) => {
                    return o.type === GuidObjectType.Identifier && reg.test(o.name);
                },
                label: 'Identifier',
                model: 'i',
                property: 'i'
            },
            {
                placeholder: 'Tag',
                isMatch: (o, reg) => {
                    return o.type === GuidObjectType.Tag && reg.test(o.name);
                },
                label: 'Tag',
                model: 't',
                property: 't'
            },
        ];

        this.isLoading = true;
        this._unsubscribe = this._storeService.register(
            store => this.refreshStore(store),
            [
                StoreElement.Dashboards,
                StoreElement.Groups,
                StoreElement.Identifiers,
                StoreElement.Tags,
                StoreElement.ValueViewModels
            ]
        );

        this._$element.on('shown.bs.dropdown', () => this.onShow());
    }

    $onDestroy() {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    private refreshStore(store: Store): void {
        const { tags, groups, valueViewModels, identifiers, dashboards } = store;

        const dashboardsVM: DisplayObject[] = _.values(dashboards.dictionary)
            .map(d => this.toViewModel(d, GuidObjectType.Dashboard, this._$state.href('dashboard', { dashboardName: d.guid })));

        const groupsVM = _.values(groups.dictionary)
            .map(g => {
                const url = this._$state.href('group.general', { groupGuid: g.guid });
                const vm = this.toViewModel(g, GuidObjectType.Group, url);
                vm.name = g.uniqueName;
                return vm;
            });

        const identifiersVM = _.values(identifiers.dictionary)
            .map(i => {
                const url = this._$state.href('linked', { linkedGuid: i.guid });
                const viewModel = valueViewModels.dictionary[i.guid] || {} as IValueViewModel;
                const vm = this.toViewModel(i, GuidObjectType.Identifier, url);
                vm.name = viewModel.groupUniqueName + groupIdentifierSeparator + i.name;
                vm.viewModel = viewModel;
                return vm;
            });

        const tagsVM = _.values(tags.dictionary)
            .map(tag => {
                const url = this._$state.href(tagDefaultState, { tagDefinitionGuid: tag.guid });
                const viewModel = valueViewModels.dictionary[tag.guid] || {} as IValueViewModel;
                const vm = this.toViewModel(tag, GuidObjectType.Tag, url);
                vm.viewModel = viewModel;
                return vm;
            });

        this.objects = [].concat(dashboardsVM, groupsVM, identifiersVM, tagsVM);
        this.isLoading = false;
    }

    private onShow() {
        this._$element.find('.searchbar-input').focus();
    }

    private toViewModel(o: VisibleObjectBase | Dashboard, type: GuidObjectType, url: string): DisplayObject {
        return {
            documentation: o.documentation,
            guid: o.guid,
            longComment: o.longComment,
            name: o.name,
            type: type,
            shortComment: o.shortComment,
            url: url
        };
    }
}

export const wtApplicationSearch: ComponentDefinition = {
    componentId: 'wtApplicationSearch',
    controller: ApplicationSearchController,
    template: require('./applicationSearch.component.html'),
    controllerAs: 'vm',
    transclude: true,
    bindings: {
    }
};